import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Printer, Package, Download, Trash2, Clock, CheckCircle2, Truck, Eye, FileText } from 'lucide-react';
import OrderChatPanel from '../components/OrderChatPanel';

const BACKEND = RUNTIME_BACKEND_URL;

const STATUS_INFO = {
  gonderildi: { label: 'Gönderildi', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  isleme_alindi: { label: 'İşleme Alındı', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Eye },
  baskida: { label: 'Baskıda', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Printer },
  kargoda: { label: 'Kargoda', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Truck },
  teslim_edildi: { label: 'Teslim Edildi', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  iptal: { label: 'İptal', color: 'bg-red-100 text-red-700 border-red-300', icon: Trash2 },
};

export default function PrintOrdersPage() {
  const { token, user } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/print-orders`, { headers });
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancelOrder = async (o) => {
    if (!window.confirm('Siparişi iptal etmek istiyor musunuz?')) return;
    try {
      await fetch(`${BACKEND}/api/print-orders/${o.id}/status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'iptal', note: 'Fotoğrafçı iptal etti' })
      });
      toast.success('Sipariş iptal edildi');
      load();
    } catch { toast.error('İptal edilemedi'); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm('Siparişi silmek istiyor musunuz?')) return;
    try {
      const res = await fetch(`${BACKEND}/api/print-orders/${o.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      toast.success('Silindi');
      load();
    } catch { toast.error('Silinemedi (işleme alınmış olabilir)'); }
  };

  return (
    <div className="space-y-4" data-testid="print-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Baskı Siparişlerim</h1>
          <p className="text-sm text-slate-500">Albümcülere gönderdiğiniz baskı siparişleri</p>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400">Yükleniyor...</div>
      ) : orders.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <Printer className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Henüz baskı siparişiniz yok</p>
          <p className="text-xs text-slate-400 mt-1">Albüm sayfasından "Albüm Baskısı İçin Gönder" butonunu kullanın</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {orders.map(o => {
            const si = STATUS_INFO[o.status] || STATUS_INFO.gonderildi;
            const SIcon = si.icon;
            return (
              <Card key={o.id} data-testid={`print-order-${o.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="h-4 w-4 text-primary" />
                        <span className="font-bold">{o.product_name}</span>
                        <span className="text-emerald-600 font-bold">{o.product_price?.toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        Albümcü: <b>{o.albumcu_name}</b>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Müşteri: {o.customer_name || '-'} · {o.wedding_date ? new Date(o.wedding_date).toLocaleDateString('tr-TR') : ''}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {new Date(o.created_at).toLocaleString('tr-TR')} · {(o.files || []).length} dosya
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${si.color}`}>
                        <SIcon className="h-3 w-3" /> {si.label}
                      </span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setSelected(o)}>
                          <Eye className="h-3.5 w-3.5 mr-1" />Detay
                        </Button>
                        {o.status === 'gonderildi' && (
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => cancelOrder(o)}>
                            İptal
                          </Button>
                        )}
                        {(o.status === 'iptal' || o.status === 'gonderildi') && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteOrder(o)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sipariş Detayı</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
                <div><b>Ürün:</b> {selected.product_name} - {selected.product_price?.toLocaleString('tr-TR')} TL</div>
                <div><b>Albümcü:</b> {selected.albumcu_name}</div>
                <div><b>Müşteri:</b> {selected.customer_name}</div>
                {selected.notes && <div><b>Not:</b> {selected.notes}</div>}
              </div>

              <div>
                <p className="text-xs font-bold mb-2 flex items-center justify-between">
                  <span>Dosyalar ({(selected.files || []).length})</span>
                  {(selected.files || []).length > 0 && (
                    <button onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND}/api/print-orders/${selected.id}/download-zip`, { headers });
                        if (!r.ok) throw new Error('HTTP ' + r.status);
                        const blob = await r.blob();
                        const cd = r.headers.get('content-disposition') || '';
                        let fname = 'siparis.zip';
                        const m = cd.match(/filename\*=UTF-8''([^;]+)/) || cd.match(/filename="([^"]+)"/);
                        if (m) try { fname = decodeURIComponent(m[1]); } catch { fname = m[1]; }
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = fname; a.style.display = 'none';
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
                      } catch (e) { toast.error('ZIP indirilemedi: ' + e.message); }
                    }}
                      className="text-xs flex items-center gap-1 text-primary font-semibold hover:underline">
                      <Download className="h-3.5 w-3.5" />ZIP İndir
                    </button>
                  )}
                </p>
                <div className="space-y-1">
                  {(selected.files || []).map((f, i) => (
                    <button key={i} onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND}/api/print-orders/${selected.id}/file/${f.stored}`, { headers });
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = f.original_name; a.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error('İndirilemedi'); }
                    }}
                      className="w-full flex items-center justify-between bg-white border rounded p-2 text-xs hover:bg-slate-50">
                      <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{f.original_name}</span>
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold mb-2">Durum Geçmişi</p>
                <div className="space-y-1">
                  {(selected.status_history || []).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-white border rounded p-2">
                      <span className="font-medium">{STATUS_INFO[h.status]?.label || h.status}</span>
                      <span className="text-slate-400">{new Date(h.at).toLocaleString('tr-TR')}</span>
                      {h.note && <span className="text-slate-500">· {h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Chat Panel */}
              <OrderChatPanel orderId={selected.id} token={token} currentUserId={user?.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
